/**
 * Follow-Up Campaign Templates
 * Based on best practices from Outreach, Salesloft, Gong, Reply.io, Woodpecker
 *
 * Key insights from billion-dollar sales engagement platforms:
 * - 80% of sales require 5+ follow-ups, yet 44% of reps give up after one
 * - Follow-ups that add value get 3x higher response rates
 * - Social proof in follow-ups increases reply rates by 15%
 * - "Breakup" emails have 76% higher response than regular follow-ups
 * - Timing: 2-3 days between emails, max 5-7 in sequence
 */

import { CampaignTemplate } from "./types";

export const FOLLOWUP_VALUE_ADD: CampaignTemplate = {
  id: "followup-value-add",
  name: "Value-Add Follow-Up",
  category: "followup",
  description:
    "Each follow-up delivers new value rather than just 'checking in'.",
  strategy:
    "Based on Gong's research showing value-adding follow-ups get 3x response rates vs. generic check-ins. Each email introduces a new resource, insight, or relevant content that helps the prospect regardless of whether they buy.",
  icon: "GiftOutlined",
  color: "#10b981",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "20-30% open rate on follow-ups",
    "5-10% reply rate (cumulative)",
    "Build trust through helpfulness",
    "Position as thought leader",
  ],

  bestPractices: [
    "Never say 'just checking in' or 'circling back'",
    "Each email should stand alone as valuable content",
    "Reference previous email context briefly",
    "Make resources genuinely useful (not gated sales content)",
    "Keep emails progressively shorter",
  ],

  objectionHandling: [
    "'Too many emails' → Acknowledge frequency, emphasize each has unique value",
    "'Not interested' → Offer to share final resource before closing loop",
    "'Using competitor' → Share comparison guide or migration success story",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    industries: ["Technology", "SaaS", "Professional Services"],
    jobTitles: [
      "VP",
      "Director",
      "Head of",
      "Manager",
      "Founder",
      "CEO",
      "CTO",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Initial Outreach",
      subject: "{{first_name}}, thought you'd find this useful",
      suggestedMjmlTemplateId: "coldOutreach-valueFirst",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Open with a valuable resource relevant to their role/industry",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "resource_name",
          defaultValue: "2024 Industry Benchmark Report",
          description: "Name of the resource being shared",
        },
        {
          key: "resource_url",
          defaultValue: "{{asset_url}}",
          description: "Link to the resource",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "Quick resource for {{company_name}}",
          "{{first_name}} - this reminded me of you",
        ],
      },
    },
    {
      name: "Day 3 - Industry Insight",
      subject: "Interesting trend in {{industry}}",
      suggestedMjmlTemplateId: "coldOutreach-insight",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share a relevant industry insight or data point",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "insight_stat",
          defaultValue: "73% of companies",
          description: "Key statistic to share",
        },
        {
          key: "insight_source",
          defaultValue: "Gartner",
          description: "Source of the insight",
        },
      ],
    },
    {
      name: "Day 7 - Tool/Template Share",
      subject: "Free template: {{template_name}}",
      suggestedMjmlTemplateId: "coldOutreach-resource",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer a practical tool or template they can use immediately",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "template_name",
          defaultValue: "ROI Calculator",
          description: "Name of the template being offered",
        },
      ],
    },
    {
      name: "Day 12 - Case Study",
      subject: "How {{case_study_company}} solved this",
      suggestedMjmlTemplateId: "coldOutreach-caseStudy",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share relevant case study from similar company",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
    },
    {
      name: "Day 17 - Final Value",
      subject: "One last resource before I go",
      suggestedMjmlTemplateId: "coldOutreach-finalValue",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Final outreach with best resource, soft close",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "{{user_title}}",
      description: "Sales rep's title",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Calendly or booking link",
      source: "crm",
    },
    {
      key: "industry",
      defaultValue: "your industry",
      description: "Prospect's industry",
      source: "enrichment",
    },
  ],

  tags: ["followup", "value-add", "nurture", "content-led", "b2b"],
  bestFor: ["Content Marketing Teams", "Consultative Sales", "Long Sales Cycles"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Gong", "HubSpot", "Drift"],
};

export const FOLLOWUP_SOCIAL_PROOF: CampaignTemplate = {
  id: "followup-social-proof",
  name: "Social Proof Sequence",
  category: "followup",
  description:
    "Build credibility through customer testimonials and social proof.",
  strategy:
    "Leverages the principle that 92% of B2B buyers are more likely to purchase after reading a trusted review. Each email introduces a different type of social proof: testimonials, logos, numbers, and peer references.",
  icon: "StarOutlined",
  color: "#f59e0b",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "15-25% open rate",
    "Increased trust and credibility",
    "Shortened sales cycle",
    "Higher meeting show rates",
  ],

  bestPractices: [
    "Use social proof from companies similar to prospect",
    "Include specific numbers and metrics in testimonials",
    "Feature recognizable logos when possible",
    "Vary the type of proof (testimonial, case study, review)",
    "Make sure quotes sound authentic, not polished",
  ],

  objectionHandling: [
    "'Our situation is different' → Find case study from similar company/industry",
    "'Those companies have bigger budgets' → Share SMB success stories",
    "'How do I know these are real?' → Offer to connect with reference customer",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "purchased"],
    companySize: ["11-50", "51-200", "201-500"],
    industries: ["Technology", "Marketing", "E-commerce", "Professional Services"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Peer Testimonial",
      subject: "What {{peer_company}} said about us",
      suggestedMjmlTemplateId: "coldOutreach-testimonial",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Share testimonial from a peer company in similar industry",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "peer_company",
          defaultValue: "{{case_study_company}}",
          description: "Similar company with testimonial",
        },
        {
          key: "testimonial_quote",
          defaultValue: "This transformed how we engage customers...",
          description: "Customer quote",
        },
        {
          key: "testimonial_author",
          defaultValue: "Sarah K., VP Marketing",
          description: "Quote attribution",
        },
      ],
    },
    {
      name: "Day 3 - Numbers & Results",
      subject: "{{result_number}} - here's how they did it",
      suggestedMjmlTemplateId: "coldOutreach-results",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share specific metrics and results achieved by customers",
      variables: [
        {
          key: "result_number",
          defaultValue: "47% increase in conversions",
          description: "Specific result achieved",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "They achieved {{result_number}} in 90 days",
          "{{result_number}} - is this possible for {{company_name}}?",
        ],
      },
    },
    {
      name: "Day 7 - Logo Wall",
      subject: "Companies like {{company_name}} trust us",
      suggestedMjmlTemplateId: "coldOutreach-logos",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Show logo wall of recognizable customers",
      variables: [
        {
          key: "customer_logos",
          defaultValue: "{{customer_logo_url}}",
          description: "Customer logo images",
        },
      ],
    },
    {
      name: "Day 12 - Industry Recognition",
      subject: "Why G2 ranked us #1 for {{category}}",
      suggestedMjmlTemplateId: "coldOutreach-recognition",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share third-party validation (awards, rankings, reviews)",
      variables: [
        {
          key: "category",
          defaultValue: "chatbot platforms",
          description: "G2/Capterra category",
        },
        {
          key: "rating",
          defaultValue: "4.8/5",
          description: "Review rating",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "case_study_company",
      defaultValue: "Acme Corp",
      description: "Reference customer name",
      source: "manual",
    },
    {
      key: "case_study_result",
      defaultValue: "40% increase in engagement",
      description: "Quantified result",
      source: "manual",
    },
  ],

  tags: ["followup", "social-proof", "testimonials", "trust-building", "b2b"],
  bestFor: ["New Market Entry", "Competitive Deals", "Enterprise Sales"],
  difficulty: "beginner",
  setupTime: "15 minutes",
  inspiredBy: ["Drift", "Intercom", "Salesforce"],
};

export const FOLLOWUP_CASE_STUDY: CampaignTemplate = {
  id: "followup-case-study",
  name: "Case Study Deep Dive",
  category: "followup",
  description: "Tell a complete customer success story across multiple touches.",
  strategy:
    "Based on Salesforce's approach of using case studies as the primary follow-up vehicle. Each email reveals a new chapter of a customer's journey: the problem, the solution, the implementation, and the results.",
  icon: "FileTextOutlined",
  color: "#8b5cf6",
  targetAudience: "enterprise",
  personalizationLevel: "high",

  expectedOutcomes: [
    "25-35% open rate (story arc creates anticipation)",
    "Higher engagement with longer-form content",
    "Prospects self-qualify based on case study fit",
    "Stronger conversations in meetings",
  ],

  bestPractices: [
    "Choose case study from company similar to prospect",
    "Break the story into digestible pieces",
    "Include specific numbers in each email",
    "End each email with a cliffhanger or question",
    "Offer full case study PDF in final email",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["51-200", "201-500", "501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing"],
    jobTitles: ["VP", "Director", "C-Level", "Head of"],
    requiredFields: ["first_name", "company_name", "email", "industry"],
  },

  emails: [
    {
      name: "Day 0 - The Problem",
      subject: "{{case_study_company}}'s biggest challenge",
      suggestedMjmlTemplateId: "coldOutreach-caseStudyProblem",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Introduce the customer and their pain point (relatable to prospect)",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "case_study_problem",
          defaultValue: "struggling to scale customer support",
          description: "The problem the customer faced",
        },
      ],
    },
    {
      name: "Day 3 - The Decision",
      subject: "Why {{case_study_company}} chose us",
      suggestedMjmlTemplateId: "coldOutreach-caseStudyDecision",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Explain evaluation criteria and why they selected your solution",
      variables: [
        {
          key: "decision_factors",
          defaultValue: "ease of implementation and AI capabilities",
          description: "Key decision factors",
        },
      ],
    },
    {
      name: "Day 7 - The Implementation",
      subject: "How {{case_study_company}} went live in {{timeframe}}",
      suggestedMjmlTemplateId: "coldOutreach-caseStudyImplementation",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Show the implementation process was smooth and fast",
      variables: [
        {
          key: "timeframe",
          defaultValue: "2 weeks",
          description: "Implementation timeline",
        },
      ],
    },
    {
      name: "Day 12 - The Results",
      subject: "{{case_study_company}}'s results after 6 months",
      suggestedMjmlTemplateId: "coldOutreach-caseStudyResults",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share the impressive outcomes with specific metrics",
      variables: [
        {
          key: "result_1",
          defaultValue: "47% reduction in support tickets",
          description: "Primary result",
        },
        {
          key: "result_2",
          defaultValue: "89% customer satisfaction",
          description: "Secondary result",
        },
      ],
    },
    {
      name: "Day 17 - Full Story + CTA",
      subject: "The full {{case_study_company}} story (PDF)",
      suggestedMjmlTemplateId: "coldOutreach-caseStudyFull",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Offer complete case study and meeting to discuss their situation",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "case_study_company",
      defaultValue: "TechCorp",
      description: "Featured customer name",
      source: "manual",
    },
    {
      key: "case_study_industry",
      defaultValue: "SaaS",
      description: "Customer industry",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["followup", "case-study", "storytelling", "enterprise", "long-form"],
  bestFor: ["Enterprise Sales", "Complex Solutions", "Consultative Selling"],
  difficulty: "intermediate",
  setupTime: "25 minutes",
  inspiredBy: ["Salesforce", "HubSpot", "Gong"],
};

export const FOLLOWUP_BREAKUP: CampaignTemplate = {
  id: "followup-breakup",
  name: "The Breakup Sequence",
  category: "followup",
  description:
    "Strategic 'breakup' emails that create urgency and get responses.",
  strategy:
    "Based on Outreach's data showing breakup emails have 76% higher response rates than regular follow-ups. Uses the psychology of loss aversion - people respond more to potential loss than potential gain. The sequence builds to a final 'closing the file' email.",
  icon: "DisconnectOutlined",
  color: "#ef4444",
  targetAudience: "b2b-saas",
  personalizationLevel: "basic",

  expectedOutcomes: [
    "40-50% open rate on breakup email",
    "10-15% response rate on final email",
    "Clear yes/no for pipeline hygiene",
    "Re-engages ghosted prospects",
  ],

  bestPractices: [
    "Don't actually sound angry or disappointed",
    "Make it easy for them to reply with a simple 'no'",
    "Leave the door open for future conversations",
    "Keep the breakup email very short (under 50 words)",
    "Use this only after 3-4 previous attempts",
  ],

  objectionHandling: [
    "'Sorry, been busy' → Great to hear from you! Quick call or should I follow up in X weeks?",
    "'Not a priority right now' → Totally understand. When should I check back?",
    "'We went another direction' → Thanks for letting me know. Any feedback for us?",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "purchased", "scraped"],
    companySize: ["11-50", "51-200", "201-500"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Soft Reminder",
      subject: "Re: {{original_subject}}",
      suggestedMjmlTemplateId: "coldOutreach-reminder",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Gentle bump referencing previous outreach",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "original_subject",
          defaultValue: "Quick question",
          description: "Subject from initial outreach",
        },
      ],
    },
    {
      name: "Day 4 - Timing Check",
      subject: "Bad timing, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-timing",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Acknowledge they may be busy, offer to reconnect later",
      abTestSuggestions: {
        subjectVariants: [
          "Should I stop reaching out?",
          "Is this a priority for {{company_name}}?",
        ],
      },
    },
    {
      name: "Day 9 - Permission to Close",
      subject: "Permission to close your file?",
      suggestedMjmlTemplateId: "coldOutreach-permissionClose",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Create urgency by implying you'll stop reaching out",
    },
    {
      name: "Day 14 - Final Breakup",
      subject: "Closing the loop",
      suggestedMjmlTemplateId: "coldOutreach-breakup",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Final email - short, direct, asks for simple yes/no",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 60,
    dailyLimit: 180,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "value_prop",
      defaultValue: "improving customer engagement with AI chatbots",
      description: "Brief value proposition reminder",
      source: "manual",
    },
  ],

  tags: ["followup", "breakup", "urgency", "pipeline-hygiene", "re-engagement"],
  bestFor: ["SDR Teams", "High-Volume Outreach", "Pipeline Cleanup"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  inspiredBy: ["Outreach", "Reply.io", "Woodpecker"],
};

export const FOLLOWUP_GENTLE_NUDGE: CampaignTemplate = {
  id: "followup-gentle-nudge",
  name: "Gentle Nudge Sequence",
  category: "followup",
  description: "Non-pushy follow-ups for warm leads who need more time.",
  strategy:
    "Based on research showing that 35-50% of sales go to the vendor that responds first AND follows up persistently. This sequence uses soft touches that maintain presence without being aggressive. Perfect for prospects who've shown interest but haven't committed.",
  icon: "BellOutlined",
  color: "#06b6d4",
  targetAudience: "generic",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "Maintain top-of-mind awareness",
    "15-20% eventual conversion",
    "Preserve relationship for future",
    "No damage to brand reputation",
  ],

  bestPractices: [
    "Space emails further apart (5-7 days)",
    "Always offer an easy out",
    "Mix value-adds with check-ins",
    "Never guilt or pressure",
    "Acknowledge their busy schedule",
  ],

  segmentation: {
    leadSource: ["website", "referral", "event", "manual"],
    companySize: ["1-10", "11-50", "51-200", "201-500"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Helpful Check-in",
      subject: "Any questions about {{product_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-helpfulCheckin",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Offer to answer questions, no pressure to buy",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "product_name",
          defaultValue: "Conferbot",
          description: "Your product name",
        },
      ],
    },
    {
      name: "Day 5 - Quick Resource",
      subject: "Thought this might help, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-quickResource",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share a relevant resource without asking for anything",
    },
    {
      name: "Day 12 - Soft Availability",
      subject: "Here when you're ready",
      suggestedMjmlTemplateId: "coldOutreach-softAvailability",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Remind you're available without pushing for a meeting",
    },
    {
      name: "Day 21 - Final Gentle Touch",
      subject: "No pressure, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-noPressure",
      delayAfterPrevious: { value: 9, unit: "days" },
      purpose: "Final check-in with easy opt-out option",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "product_name",
      defaultValue: "Conferbot",
      description: "Your product name",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["followup", "gentle", "nurture", "low-pressure", "warm-leads"],
  bestFor: ["Relationship-Based Sales", "High-Value Deals", "Long Sales Cycles"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  inspiredBy: ["HubSpot", "Intercom", "Close"],
};

export const FOLLOWUP_OBJECTION_HANDLER: CampaignTemplate = {
  id: "followup-objection-handler",
  name: "Objection Handler Sequence",
  category: "followup",
  description:
    "Proactively address common objections before they derail the deal.",
  strategy:
    "Based on Gong's analysis of millions of sales calls identifying the top objections that kill deals. This sequence preemptively addresses concerns about price, timing, competition, and implementation before the prospect brings them up.",
  icon: "SafetyCertificateOutlined",
  color: "#6366f1",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "Reduce objections in sales calls by 40%",
    "Faster deal progression",
    "More qualified meetings",
    "Higher close rates",
  ],

  bestPractices: [
    "Address objections before they're raised",
    "Use proof points for each objection",
    "Don't sound defensive - be matter-of-fact",
    "One objection per email",
    "End with forward motion, not apology",
  ],

  objectionHandling: [
    "'Too expensive' → Share ROI calculator and payback period",
    "'Not the right time' → Show cost of waiting/inaction",
    "'Happy with current solution' → Share switching success stories",
    "'Need more features' → Demonstrate roadmap and customization",
    "'Implementation concerns' → Highlight support and success team",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    industries: ["Technology", "E-commerce", "Professional Services"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - ROI/Value",
      subject: "The real cost of {{pain_point}}",
      suggestedMjmlTemplateId: "coldOutreach-roiValue",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Address price concerns by showing ROI and payback",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "pain_point",
          defaultValue: "manual customer support",
          description: "The costly problem they face",
        },
        {
          key: "roi_stat",
          defaultValue: "287% ROI in year one",
          description: "ROI statistic",
        },
      ],
    },
    {
      name: "Day 4 - Implementation Ease",
      subject: "Live in {{timeframe}} - here's how",
      suggestedMjmlTemplateId: "coldOutreach-implementation",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Address implementation concerns with timeline and support",
      variables: [
        {
          key: "timeframe",
          defaultValue: "1 week",
          description: "Typical implementation time",
        },
      ],
    },
    {
      name: "Day 8 - Switching Story",
      subject: "Why {{switcher_company}} left {{competitor}}",
      suggestedMjmlTemplateId: "coldOutreach-switchingStory",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Address competition concerns with migration success story",
      variables: [
        {
          key: "switcher_company",
          defaultValue: "Acme Corp",
          description: "Company that switched",
        },
        {
          key: "competitor",
          defaultValue: "Intercom",
          description: "Previous solution they left",
        },
      ],
    },
    {
      name: "Day 13 - Timing/Urgency",
      subject: "What waiting 6 months costs {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-costOfWaiting",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Address timing objection with cost of inaction",
      variables: [
        {
          key: "delay_cost",
          defaultValue: "$50,000 in lost efficiency",
          description: "Estimated cost of delaying",
        },
      ],
    },
    {
      name: "Day 18 - Risk Reversal",
      subject: "Zero risk to try - here's our guarantee",
      suggestedMjmlTemplateId: "coldOutreach-riskReversal",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Remove final barriers with guarantee or trial offer",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 45,
    dailyLimit: 135,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "pain_point",
      defaultValue: "customer support efficiency",
      description: "Main pain point",
      source: "manual",
    },
    {
      key: "competitor",
      defaultValue: "your current solution",
      description: "Competitor or current solution",
      source: "enrichment",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["followup", "objection-handling", "roi", "competitive", "enterprise"],
  bestFor: ["Competitive Markets", "High-Value Deals", "Enterprise Sales"],
  difficulty: "advanced",
  setupTime: "25 minutes",
  inspiredBy: ["Gong", "Challenger Sale", "MEDDIC"],
};

export const FOLLOWUP_TEMPLATES = [
  FOLLOWUP_VALUE_ADD,
  FOLLOWUP_SOCIAL_PROOF,
  FOLLOWUP_CASE_STUDY,
  FOLLOWUP_BREAKUP,
  FOLLOWUP_GENTLE_NUDGE,
  FOLLOWUP_OBJECTION_HANDLER,
];
